import { useState } from 'react';
import styled from 'styled-components';
import { combineLatest } from 'rxjs';
import { connect, spreadMap } from '@ardoq/rxbeach';
import { AiInlineInput } from '@ardoq/ai';
import type { AiControlledCommand } from 'aiControlledCommands/types';
import type { ConditionalFormattingAttributes } from '@ardoq/api-types';
import { IconButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale } from '@ardoq/i18n';
import { availableChatAssistantTools$ } from 'assistant/tools/availableChatAssistantTools';
import { commands as assistantCommands } from 'assistant/commands';
import { assistantMode$ } from 'assistant/assistantState$';
import { AssistantMode } from 'assistant/assistantTypes';
import { trackAssistantEvent } from 'assistant/trackAssistantEvent';
import { AssistantTrackingEvents } from 'assistant/trackingEvents';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { conditionalFormattingFilters$ } from 'streams/filters/conditionalFormattingFilters$';
import { isUpdatingAppState$ } from 'isUpdatingAppState$';

const DISMISSED_STORAGE_KEY = 'mockup:viewAiBarDismissed';

type ViewAiBarProps = {
  isAvailable: boolean;
  i18nLocale: I18nLocale;
};

export const ViewAiBar = ({ isAvailable, i18nLocale }: ViewAiBarProps) => {
  const [prompt, setPrompt] = useState('');
  const [isDismissed, setIsDismissed] = useState(
    () => localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true'
  );

  if (!isAvailable || isDismissed) {
    return null;
  }

  const submit = () => {
    const content = prompt.trim();
    if (!content) {
      return;
    }
    assistantCommands.openAndSend(content, 'viewPromptBar');
    setPrompt('');
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
    trackAssistantEvent(AssistantTrackingEvents.DISMISSED_VIEW_PROMPT_BAR_AI);
    setIsDismissed(true);
  };

  return (
    <FlexBox
      align="center"
      gap="small"
      paddingY="small"
      paddingX="medium"
      backgroundColor="surfaceDefault"
      borderColor="borderSubtle"
      borderWidthBottom={1}
    >
      <InputSlot>
        <AiInlineInput
          i18nLocale={i18nLocale}
          input={{
            value: prompt,
            placeholder: t(
              i18nLocale,
              'appLayout:aiPromptBarPlaceholder',
              'Ask AI to set up this view, e.g. "color apps nearing end of life"'
            ),
            onValueChange: setPrompt,
            'aria-label': t(
              i18nLocale,
              'appLayout:aiPromptBarInputLabel',
              'Ask AI to set up this view'
            ),
          }}
          generateButton={{
            label: t(i18nLocale, 'appLayout:aiPromptBarSubmit', 'Ask AI'),
            onClick: submit,
            isDisabled: prompt.trim() === '',
          }}
        />
      </InputSlot>
      <IconButton
        iconName={IconName.CLOSE}
        label={t(i18nLocale, 'appLayout:aiPromptBarHide', 'Hide AI bar')}
        onClick={dismiss}
      />
    </FlexBox>
  );
};

const InputSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

export const toViewAiBarProps = (
  commands: AiControlledCommand[],
  assistantMode: AssistantMode,
  formattingFilters: ConditionalFormattingAttributes[],
  isUpdatingAppState: boolean,
  i18nLocale: I18nLocale
): ViewAiBarProps => ({
  isAvailable:
    commands.length > 0 &&
    assistantMode !== AssistantMode.Open &&
    formattingFilters.length === 0 &&
    !isUpdatingAppState,
  i18nLocale,
});

const viewAiBarProps$ = combineLatest([
  availableChatAssistantTools$,
  assistantMode$,
  conditionalFormattingFilters$,
  isUpdatingAppState$,
  i18nLocale$,
]).pipe(spreadMap(toViewAiBarProps));

export const ConnectedViewAiBar = connect(ViewAiBar, viewAiBarProps$);
